'use client';

import { createContext, useContext } from 'react';
import { cn } from '@utils';
import { tv } from 'tailwind-variants';

type CalloutContextType = {
	variant?: 'default';
};

const CalloutContext = createContext<CalloutContextType>({
	variant: 'default',
});

type CalloutProps = {
	variant?: 'default';
	children: React.ReactNode;
	className?: string;
};

const Callout = ({ children, variant, className }: CalloutProps) => {
	const calloutStyle = tv({
		base: ['py-6 pr-4 pl-6', 'border-light-design-accent flex min-h-30 items-center border-l-4'],
		variants: {
			variant: {
				default: 'bg-neutral-lightest',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	});
	return (
		<CalloutContext.Provider value={{ variant }}>
			<div className={cn(calloutStyle({ variant }), className)}>{children}</div>
		</CalloutContext.Provider>
	);
};

const CalloutContent = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	return <div className={cn('flex flex-col gap-4', className)}>{children}</div>;
};

const CalloutTitle = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	const { variant } = useContext(CalloutContext);

	const titleStyle = tv({
		variants: {
			variant: {
				default: 'text-light-heading',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	});
	return <h3 className={cn(titleStyle({ variant }), className)}>{children}</h3>;
};

const CalloutBody = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	const { variant } = useContext(CalloutContext);

	const bodyStyle = tv({
		variants: {
			variant: {
				default: 'text-light-text',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	});

	return <div className={cn(bodyStyle({ variant }), className)}>{children}</div>;
};

Callout.Content = CalloutContent;
Callout.Title = CalloutTitle;
Callout.Body = CalloutBody;

export { Callout };
